import { Colors } from '@kvika/audur-theme'
import { PrismicRichText, PrismicRichTextProps } from '@prismicio/react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'react-responsive'

import { GridItem, MainGridContainer } from '../../components/grid-container'
import { linkResolver } from '../../prismic/prismic-configuration'
import { HeroWithCtaSlice } from '../../prismic/prismicTypes'
import { TranslatedStrings } from '../../utils/translations/TranslatedStrings'
import { ColumnClass } from '../../utils/types/Types'
import { AudurButton } from '../../components/audurButtons'
import { Checkmark } from '../../../public/icons/Icons'

import styles from './mortgageSlices.module.scss'

type IMortgageHeroCTASlice = {
	displayIconButton: boolean
	hideImageMobile: boolean
	firstButtonLink: string
	secondButtonLink?: string
	isSubpage?: boolean
	hasFAQ?: boolean
} & HeroWithCtaSlice

const MortgageHeroCTASlice = ({
	displayIconButton,
	hideImageMobile,
	firstButtonLink,
	secondButtonLink,
	isSubpage = false,
	hasFAQ = false,
	primary: { hero_title, description, image, apply_for_mortage_button, loan_calculator_button, color, disclamer },
}: IMortgageHeroCTASlice) => {
	const router = useRouter()
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 979px)' })

	const getMainGridContainerStyle = () => {
		if (isTabletOrMobile) {
			return { gridTemplateColumns: '0fr 1fr 0fr' }
		}
		if (isSubpage && !hasFAQ) {
			return { padding: '80px 0' }
		}
		if (isSubpage && hasFAQ) {
			return { padding: '80px 0', marginBottom: '80px' }
		}
		return undefined
	}

	return (
		<MainGridContainer
			backgroundColor={color === 'DarkGray' ? Colors.DarkGray : Colors.White}
			style={getMainGridContainerStyle()}
		>
			<GridItem className={ColumnClass.All}>
				<div
					className={`${styles.heroCTA} ${color === 'Beige' && styles.beige} ${isSubpage && styles.subpage}`}
					data-testid='mortgage-hero-cta'
				>
					<div className={styles.heroCTALeft} data-testid='hero-cta-left'>
						{hero_title && hero_title[0] && <h1 data-testid='hero-title'>{hero_title[0].text}</h1>}
						<div data-testid='hero-description'>
							{description && description[0] ? (
								<PrismicRichText
									field={description}
									components={{
										paragraph: ({ children }) => <p className={styles.description}>{children}</p>,
										list: ({ children }) => <ul className={styles.bulletList}>{children}</ul>,
										listItem: ({ children }) => (
											<li>
												<Checkmark color={Colors.Red} />
												{children}
											</li>
										),
									}}
								/>
							) : (
								TranslatedStrings.is.mortgagePage.heroDefaultDescription
							)}
						</div>
						<div
							className={`${styles.heroCTAButtons} ${!displayIconButton && styles.displayTextButtons}`}
							data-testid='hero-cta-buttons'
						>
							<AudurButton
								onClick={() => router.push(firstButtonLink)}
								size={isSubpage ? 'medium' : 'large'}
								variant='filled'
								backgroundType={color === 'Beige' ? 'light' : 'dark'}
							>
								{apply_for_mortage_button ?? TranslatedStrings.is.mortgagePage.heroDefaultButton}
							</AudurButton>
							<AudurButton
								size={isSubpage ? 'medium' : 'large'}
								variant='outlined'
								backgroundType={color === 'Beige' ? 'light' : 'dark'}
								onClick={() =>
									secondButtonLink
										? router.push(secondButtonLink)
										: document.getElementById('loan_calculator')?.scrollIntoView({ behavior: 'smooth' })
								}
							>
								{displayIconButton && (
									<Image
										src='/svg/calculatorIcon.svg'
										width={24}
										alt='Calculator icon'
										height={24}
										className={styles.buttonIcon}
										priority
									/>
								)}
								<span>{loan_calculator_button ?? TranslatedStrings.is.mortgagePage.heroDefaultCalculatorButton}</span>
							</AudurButton>
						</div>
						<div className={styles.disclamer}>
							{Array.isArray(disclamer) ? (
								<PrismicRichText
									field={disclamer as PrismicRichTextProps['field']}
									components={{
										hyperlink: ({ node, children }) => {
											if (node.data.link_type === 'Document') {
												const url =
													node.data.type === 'mortgage_sub_page'
														? `/husnaedislan/${node.data.uid}`
														: linkResolver(node.data)
												return <Link href={url}>{children}</Link>
											}
											// External/Web links
											const target = 'target' in node.data ? node.data.target : '_blank'
											return (
												<a href={node.data.url} target={target || '_blank'} rel='noopener noreferrer'>
													{children}
												</a>
											)
										},
									}}
								/>
							) : (
								disclamer
							)}
						</div>
					</div>
					<div className={`${styles.heroCTARight} ${hideImageMobile && styles.hideImageMobile}`}>
						{image && (
							<Image
								className={styles.heroCTAImage}
								src={image.url}
								alt={image.alt}
								width={image.dimensions.width}
								height={image.dimensions.height}
								priority
							/>
						)}
					</div>
				</div>
			</GridItem>
		</MainGridContainer>
	)
}

export default MortgageHeroCTASlice
